'use client';

import { useMemo, useState } from 'react';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import { useTrackPricingCardClick } from '@/hooks/useTrackPricingCardClick';

import { __hack__strapiRelationsOrderReverse } from '@/app/utils';
import Features, {
  featuresMapToProductType,
  FeaturesProps,
} from '@/blocks/PricingCards/Features';
import { PaymentMethods } from '@/blocks/PricingCards/PaymentMethods';
import { PricingCardProps } from '@/blocks/PricingCards/PricingCard';
import {
  PricingCardContactSalesProps,
  PricingCardsFeatureListStrapi,
} from '@/blocks/PricingCards/PricingCardsStrapi';
import {
  Product,
  useProductsPricingCardsText,
} from '@/blocks/PricingCards/product';
import Banner from '@/components/atoms/banner';
import { BannerStrapiProps } from '@/components/atoms/banner/BannerStrapi';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import RichTextFieldStrapi, {
  ExtendedLinkProps,
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';
import { applyToAllRTFVariants } from '@/components/atoms/RichTextField/utils';
import Switch from '@/components/atoms/Switch';
import { Toggle, ToggleProps } from '@/components/atoms/Toggle';

import { SvgIcon } from '@/components/SvgIcon';
import Button from '@/components/atoms/Button';
import iconArrowRightSrc from '~/icons/arrow-right.svg';
import iconApproveCircleSrc from '~/icons/check-circle.svg';
import iconChevronDownSrc from '~/icons/chevron-down.svg';
import iconContactSrc from '~/icons/message-chat-circle.svg';
import iconContactSalesSrc from '~/svg/IconContactSales.svg';
import iconHandHoldingSrc from '~/svg/IconHandHolding.svg';
import {
  PricingCardsDisplay,
  PricingCardsDisplayProps,
} from './PricingCardsDisplay';

function getCardCtaSections(
  pricingCard: { buttonText?: string; url?: string; buttonVariant?: string },
  contactSalesText: string
) {
  const effectiveText = pricingCard.buttonText || contactSalesText;
  const isContactSalesText = effectiveText === contactSalesText;
  const icon =
    pricingCard.url === '#contact-sales' ? (
      <SvgIcon src={iconContactSalesSrc} className='h-5 w-5' />
    ) : (
      <SvgIcon src={iconArrowRightSrc} className='h-5 w-5' />
    );

  return {
    buttonText: effectiveText,
    buttonVariant: (pricingCard.buttonVariant || 'filled') as
      | 'filled'
      | 'outlined',
    url: pricingCard.url || '#contact-sales',
    ctaLeftSection: isContactSalesText ? icon : undefined,
    ctaRightSection: !isContactSalesText ? icon : undefined,
  };
}

export type ProductPriceTypes = 'regular' | 'enterprise';
export type ProductSequantialTypes = 'sequential' | 'non-sequential';

export type PromoTextProps = {
  text?: RichTextFieldStrapiProps;
  color: 'green' | 'red' | 'black';
  image?: ImageStrapiProps;
};

export type PricingCardsProps = {
  cards: {
    productType?: Product;
    productPriceCategory: ProductPriceTypes;
    productSequence?: ProductSequantialTypes;
    hideUnderExpandable?: boolean;
    hiddenButProductTypeShown?: boolean;
    isContactCard?: boolean;
    pricingCard: PricingCardProps;
  }[];
  contactSalesCards?: PricingCardContactSalesProps;
  features?: PricingCardsFeatureListStrapi['featuresList'];
  toggleVariant: ToggleProps['variant'];
  promoText?: PromoTextProps;
  disclaimer?: RichTextFieldStrapiProps;
  cardLayout?: 'three_cols' | 'four_cols';
  banner?: BannerStrapiProps;
};

function groupByProductCategory<T extends string>(
  cards: PricingCardsProps['cards'],
  searchByKey: 'productType' | 'productPriceCategory' | 'productSequence'
): Record<T, PricingCardsProps['cards']> {
  return cards.reduce((acc, card) => {
    if (!card || !card[searchByKey]) {
      return acc;
    }

    const key = card[searchByKey] as T;

    if (key in acc) {
      acc[key].push(card);
    } else if (card[searchByKey]) {
      acc[key] = [card];
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      acc['default'] = acc['default'] ? acc['default'].concat(card) : [card];
    }
    return acc;
  }, {} as Record<T, PricingCardsProps['cards']>);
}

export default function PricingCards({
  cards,
  features,
  contactSalesCards,
  toggleVariant,
  promoText,
  disclaimer,
  cardLayout,
  banner,
}: PricingCardsProps) {
  const { texts } = useTranslation();
  const productsPricingCardsText = useProductsPricingCardsText();

  const [showMorePlans, setShowMorePlans] = useState(false);

  const priceText = {
    regular: texts.pricing_cards.regular,
    enterprise: texts.pricing_cards.enterprise,
  } as const;

  const [productSequential, setProductSequential] =
    useState<ProductSequantialTypes>('sequential');
  const [productPriceType, setProductPriceType] =
    useState<ProductPriceTypes>('regular');

  const cardsGroupedByProductType: {
    [key in Product]: PricingCardsProps['cards'];
  } = groupByProductCategory<Product>(cards, 'productType');
  const cardsGroupedByProductPriceType: {
    [key in ProductPriceTypes]: PricingCardsProps['cards'];
  } = groupByProductCategory<ProductPriceTypes>(cards, 'productPriceCategory');

  const [selectedProduct, setProduct] = useState<Product>(
    Object.keys(cardsGroupedByProductType)[0] as Product
  );

  const trackId = `pricing-cards-toggle-${selectedProduct}`;

  const trackPriceTypeToggleClick = useTrackPricingCardClick({
    trackEventName: 'pricing_cards_price_type_toggle_click',
    trackId,
  });
  const trackShowMorePlansClick = useTrackPricingCardClick({
    trackEventName: 'pricing_cards_show_more_plans_click',
    trackId,
  });

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  !!cardsGroupedByProductType?.none && delete cardsGroupedByProductType.none;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  !!cardsGroupedByProductType?.ecommerce &&
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    delete cardsGroupedByProductType.ecommerce;

  function checkIfOneProduct() {
    return Object.keys(cardsGroupedByProductType).length === 1;
  }

  function checkIfMultipleProducts() {
    return Object.keys(cardsGroupedByProductType).length > 1;
  }

  function checkIfHasMorePriceTypes() {
    return Object.keys(cardsGroupedByProductPriceType).length > 1;
  }

  function checkIfProductHasSequentialAndNonSequantial() {
    if (!selectedProduct) return false;

    const hasSequential = cardsGroupedByProductType[selectedProduct]?.some(
      (card) => card.productSequence === 'sequential'
    );
    const hasNonSequential = cardsGroupedByProductType[selectedProduct]?.some(
      (card) => card.productSequence === 'non-sequential'
    );

    return hasSequential && hasNonSequential;
  }

  function checkIfProductHasMorePriceCategory() {
    if (!selectedProduct) return false;

    const hasEnterprise = cardsGroupedByProductType[selectedProduct]?.some(
      (card) => card.productPriceCategory === 'enterprise'
    );
    const hasRegular = cardsGroupedByProductType[selectedProduct]?.some(
      (card) => card.productPriceCategory === 'regular'
    );

    return hasEnterprise && hasRegular;
  }

  function handleEnterpriseToggle() {
    setProductPriceType(
      productPriceType === 'regular' ? 'enterprise' : 'regular'
    );
  }

  function handleSequentialToggle() {
    setProductSequential(
      productSequential === 'sequential' ? 'non-sequential' : 'sequential'
    );
  }

  const hasBothPriceCategories = checkIfProductHasMorePriceCategory();

  const getCardsForPriceType = (priceType: ProductPriceTypes) => {
    const filteredByPrice = hasBothPriceCategories
      ? cardsGroupedByProductType[selectedProduct].filter(
          (card) => card.productPriceCategory === priceType
        )
      : cardsGroupedByProductType[selectedProduct];

    const filteredBySequential =
      checkIfProductHasSequentialAndNonSequantial()
        ? filteredByPrice.filter(
            (card) => card.productSequence === productSequential
          )
        : filteredByPrice;

    return {
      shown:
        filteredBySequential?.filter(
          (card) => !card.hideUnderExpandable && !card.isContactCard
        ) || [],
      expandable:
        filteredBySequential?.filter(
          (card) => card.hideUnderExpandable && !card.isContactCard
        ) || [],
    };
  };

  const { shown: shownCards, expandable: expandableCards } =
    getCardsForPriceType(productPriceType);

  const hasExpandableCards = expandableCards.length > 0;

  const priceTypesToRender: ProductPriceTypes[] = hasBothPriceCategories
    ? ['regular', 'enterprise']
    : [productPriceType];

  const getContactSalesForPriceType = (priceType: ProductPriceTypes) =>
    contactSalesCards?.data?.find(({ attributes: att }) => {
      const productMatches = att.productType === selectedProduct;
      const priceCategoryMatches =
        att.productPriceCategory === priceType ||
        att.productPriceCategory === 'all';
      const sequentialMatches =
        att.productSequence === productSequential ||
        att.productSequence === 'all';

      return productMatches && priceCategoryMatches && sequentialMatches;
    })?.attributes;

  let selectedFeatures: Omit<FeaturesProps, 'inRow'> | undefined;

  if (features?.data?.length) {
    const filteredFeature = __hack__strapiRelationsOrderReverse<
      (typeof features.data)[number]
    >(features.data).find(
      (feature) => feature.attributes.productType === selectedProduct
    );

    if (filteredFeature) {
      selectedFeatures = {
        icon: filteredFeature.attributes.icon ? (
          <ImageStrapi
            block={filteredFeature.attributes.icon}
            inner={{ classNames: { image: 'w-4 h-4' } }}
          />
        ) : undefined,
        title: filteredFeature.attributes.title,
        features:
          filteredFeature.attributes.features?.map(
            ({ name }: { name: string }) => ({
              name,
            })
          ) || [],
      };
    }
  }

  if (!selectedFeatures) {
    const filteredFeature =
      featuresMapToProductType?.[selectedProduct]?.(texts);
    if (filteredFeature) {
      selectedFeatures = {
        icon: (
          <SvgIcon
            src={iconApproveCircleSrc}
            className='text-success-500 h-4 w-4'
          />
        ),
        features: filteredFeature.map(({ feature }) => ({ name: feature })),
      };
    }
  }

  const hasSelectedEnterprise = productPriceType === 'enterprise';
  const isResidentialProductType = useMemo(
    () => cards.some((card) => card.productType === 'residential'),
    [cards]
  );

  const hasSpecialBadge = shownCards.some(
    (card) => !!card.pricingCard.outlineText
  );

  const selectedControlledSalesCardToRender =
    getContactSalesForPriceType(productPriceType);

  const hasMoneyBackGuarantee = [
    ...shownCards,
    ...(showMorePlans ? expandableCards : []),
  ].some((card) => card.pricingCard.showMoneyBack);

  const hasDatacenterIpToggle = Object.keys(cardsGroupedByProductType).includes(
    'datacenter_ip'
  );

  const isSequentialSwitchShown =
    checkIfProductHasMorePriceCategory() && checkIfMultipleProducts();

  const promoColorMap = {
    red: 'text-error-700',
    green: 'text-success-700',
    black: 'text-base-black',
  };

  const ConditionalBanner = banner ? (
    <Banner
      shouldTrackEvent
      classNames={{
        root: cn(
          'w-full !bg-brand-50 dark:!bg-brand-975',
          hasExpandableCards ? 'mt-8' : 'md:mb-8 md:mt-16 my-8'
        ),
      }}
      button={banner.buttons?.[0]}
      buttonIcon={<SvgIcon src={iconContactSrc} className='h-[20px]' />}
      description={
        banner.description ? (
          <RichTextFieldStrapi block={banner.description} />
        ) : undefined
      }
      title={banner.title}
      direction={banner.direction}
      image={
        <ImageStrapi
          inner={{ classNames: { image: 'w-8' } }}
          block={{ ...banner.image }}
        />
      }
      countdownExpiry={banner.countdownExpiry}
    />
  ) : null;

  const pricingCardsDisplayProps: Omit<PricingCardsDisplayProps, 'cards'> = {
    getCardCtaSections,
    cardLayout,
    hasDatacenterIpToggle,
    hasSpecialBadge,
    hasSelectedEnterprise,
    isSequentialSwitchShown,
    isResidentialProductType,
    promoText,
    selectedProduct,
    selectedControlledSalesCardToRender,
    texts,
  };

  return (
    <div className='flex w-full flex-col items-center'>
      {!hasExpandableCards && (
        <div
          className={cn(
            'flex w-full flex-col items-center justify-center',
            isResidentialProductType ? 'gap-0' : 'gap-6'
          )}
        >
          {checkIfMultipleProducts() && (
            <Toggle
              options={Object.keys(cardsGroupedByProductType).map(
                (product) => productsPricingCardsText[product as Product]
              )}
              selectedIndex={Object.keys(cardsGroupedByProductType).indexOf(
                selectedProduct
              )}
              onValueChange={(index) => {
                const product = Object.keys(cardsGroupedByProductType)[index];
                setProductPriceType('regular');
                setProduct(product as Product);
              }}
              variant={toggleVariant}
            />
          )}
          {checkIfOneProduct() && checkIfHasMorePriceTypes() && (
            <Toggle
              options={Object.keys(cardsGroupedByProductPriceType)
                .sort()
                .reverse()
                .map((priceType) => priceText[priceType as ProductPriceTypes])}
              selectedIndex={productPriceType === 'regular' ? 0 : 1}
              onValueChange={(index) => {
                const priceType = index === 0 ? 'regular' : 'enterprise';
                setProductPriceType(priceType);
                trackPriceTypeToggleClick({
                  button_text: priceText[priceType],
                });
              }}
              variant={toggleVariant}
            />
          )}
          {((checkIfProductHasMorePriceCategory() &&
            checkIfMultipleProducts()) ||
            checkIfProductHasSequentialAndNonSequantial() ||
            hasSelectedEnterprise) && (
            <div className='flex w-full flex-wrap items-center justify-start gap-6 md:justify-center'>
              {checkIfProductHasMorePriceCategory() &&
                checkIfMultipleProducts() && (
                  <Switch
                    isOn={hasSelectedEnterprise}
                    onToggle={handleEnterpriseToggle}
                    startLabel={texts.pricing_cards.enterprise}
                    ButtonProps={{
                      'data-tracker-context': 'product-multi-price-category',
                    }}
                  />
                )}
              {checkIfProductHasSequentialAndNonSequantial() && (
                <Switch
                  isOn={productSequential === 'non-sequential'}
                  onToggle={handleSequentialToggle}
                  startLabel={texts.pricing_cards.non_sequential}
                  ButtonProps={{
                    'data-tracker-context': 'pricing-cards-non-sequential',
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}
      {hasSelectedEnterprise && !isResidentialProductType && (
        <div className='mt-6 md:mt-0 flex w-full basis-full justify-start gap-3 md:justify-center'>
          <SvgIcon src={iconHandHoldingSrc} />
          {texts.pricing_cards.dedicated_account_manager_included}
        </div>
      )}
      {promoText?.text && (
        <div className='flex gap-2'>
          {promoText.image && (
            <ImageStrapi
              block={promoText.image}
              inner={{ classNames: { image: 'h-6 w-6' } }}
            />
          )}
          <div
            className={cn(
              'font-mono',
              promoColorMap[promoText.color] || promoColorMap.black
            )}
          >
            <RichTextFieldStrapi block={promoText.text} />
          </div>
        </div>
      )}
      {priceTypesToRender.map((priceType) => {
        const { shown } = getCardsForPriceType(priceType);
        const isActive = priceType === productPriceType;
        const displayProps: PricingCardsDisplayProps = {
          ...pricingCardsDisplayProps,
          cards: shown,
          hasSelectedEnterprise: priceType === 'enterprise',
          selectedControlledSalesCardToRender:
            getContactSalesForPriceType(priceType),
        };

        return (
          <div
            key={priceType}
            className={cn('w-full', !isActive && 'hidden')}
          >
            <PricingCardsDisplay {...displayProps} />
          </div>
        );
      })}
      {disclaimer && (disclaimer.text?.length || disclaimer.text2) && (
        <div
          className={cn(
            'mt-6 w-full text-left md:mt-8',
            cardLayout === 'four_cols'
              ? 'mx-auto'
              : 'mx-auto lg:max-w-[1024px]'
          )}
        >
          <RichTextFieldStrapi
            block={disclaimer}
            classNames={{
              ...applyToAllRTFVariants(
                'text-body-m text-gray-600 dark:text-base-dark-white!'
              ),
              link: {
                root: 'text-body-m text-gray-600 dark:text-base-dark-white!',
              },
            }}
            extraProps={{
              link: {
                showExternalLinkIcon: true,
              } as ExtendedLinkProps,
            }}
          />
        </div>
      )}
      {hasExpandableCards && (
        <div
          className='flex items-center w-full mt-12 gap-4'
          role='separator'
          aria-orientation='horizontal'
        >
          <hr aria-hidden='true' className='flex-1 border-t border-gray-200' />
          <Button
            variant='outlined'
            color='secondary'
            rightSection={
              <SvgIcon
                src={iconChevronDownSrc}
                className={cn(
                  'transition-transform',
                  showMorePlans && 'rotate-180'
                )}
              />
            }
            onClick={() => {
              trackShowMorePlansClick({
                button_text: texts.pricing_cards.show_more_plans,
              });
              setShowMorePlans((prev) => !prev);
            }}
            aria-expanded={showMorePlans}
            aria-controls='pricing-cards-expandable'
          >
            {texts.pricing_cards.show_more_plans}
          </Button>
          <hr aria-hidden='true' className='flex-1 border-t border-gray-200' />
        </div>
      )}
      {hasExpandableCards && showMorePlans && (
        <div id='pricing-cards-expandable' className='w-full'>
          <PricingCardsDisplay
            {...pricingCardsDisplayProps}
            cards={expandableCards}
          />

          {ConditionalBanner}
        </div>
      )}
      {!hasExpandableCards && ConditionalBanner}
      {!!selectedFeatures?.features?.length && (
        <div
          className={cn(
            'w-full',
            banner ? 'mt-0' : 'mt-9 md:mt-[3.75rem]',
            hasExpandableCards && 'mt-12 mb-6 md:mt-16 md:mb-1'
          )}
        >
          <Features {...selectedFeatures} />
        </div>
      )}
      <PaymentMethods
        product={selectedProduct}
        showMoneyBack={hasMoneyBackGuarantee}
      />
    </div>
  );
}
